import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import BookConsultationButton from '../components/BookConsultationButton';
import Section from '../components/ui/Section';
import Container from '../components/ui/Container';
import Button from '../components/ui/Button';
import { CalendarIcon, CheckIcon, MailIcon } from '../components/ui/Icons';
import usePageMeta from '../hooks/usePageMeta';
import pages from '../content/pages.json';
import { ContactFieldErrors, ContactFormData, sendContactEmail } from '../utils/email';
import { CONTACT_EMAIL } from '../data/site';

const SERVICE_OPTIONS = [
  { value: 'website', label: 'Website development' },
  { value: 'mobile', label: 'Mobile app development' },
  { value: 'cybersecurity', label: 'Cybersecurity' },
  { value: 'cloud-devops', label: 'Cloud & DevOps' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'ux-ui', label: 'UX / UI design' },
  { value: 'maintenance', label: 'Maintenance & support' },
  { value: 'other', label: 'Other' },
];

const LIMITS = { name: 120, company: 120, message: 4000 };
const EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/;

type FormState = Required<Omit<ContactFormData, 'website'>> & { website: string; consent: boolean };
type FieldErrors = ContactFieldErrors & { consent?: string };

const INITIAL: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  service: '',
  message: '',
  website: '',
  consent: false,
};

const controlValue = (id: string) =>
  (document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null)?.value ?? '';

// Typing before hydration leaves the DOM filled and state empty. Copy the fields in on mount.
const readFormFromDom = (): FormState | null => {
  if (typeof document === 'undefined' || !document.getElementById('firstName')) return null;
  const consent = document.getElementById('consent') as HTMLInputElement | null;
  return {
    firstName: controlValue('firstName'),
    lastName: controlValue('lastName'),
    email: controlValue('email'),
    company: controlValue('company'),
    service: controlValue('service'),
    message: controlValue('message'),
    website: controlValue('website'),
    consent: Boolean(consent?.checked),
  };
};

const sameForm = (left: FormState, right: FormState) =>
  (Object.keys(INITIAL) as (keyof FormState)[]).every((key) => left[key] === right[key]);

const validate = (data: FormState): FieldErrors => {
  const errors: FieldErrors = {};
  if (!data.firstName.trim()) errors.firstName = 'First name is required.';
  else if (data.firstName.length > LIMITS.name) errors.firstName = `Keep it under ${LIMITS.name} characters.`;
  if (!data.lastName.trim()) errors.lastName = 'Last name is required.';
  else if (data.lastName.length > LIMITS.name) errors.lastName = `Keep it under ${LIMITS.name} characters.`;
  if (!data.email.trim()) errors.email = 'Email is required.';
  else if (!EMAIL_PATTERN.test(data.email.trim())) errors.email = 'Enter a valid email address.';
  if (data.company.length > LIMITS.company) errors.company = `Keep it under ${LIMITS.company} characters.`;
  if (!data.service) errors.service = 'Select what you are interested in.';
  if (!data.message.trim()) errors.message = 'Tell us a little about your project.';
  else if (data.message.length > LIMITS.message) errors.message = `Keep it under ${LIMITS.message} characters.`;
  if (!data.consent) errors.consent = 'Please confirm we can contact you about your inquiry.';
  return errors;
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  gap: clamp(2rem, 5vw, 4rem);
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Side = styled.div`
  display: grid;
  gap: 1rem;
`;

const SideCard = styled.div`
  padding: clamp(1.5rem, 3vw, 2rem);
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--border);

  h2 {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 1.25rem;
    margin-bottom: 0.6rem;
  }

  h2 svg {
    color: var(--accent);
  }

  p {
    color: var(--text-2);
    margin-bottom: 1.25rem;
  }

  a.email {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: var(--text-1);
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 4px;
    word-break: break-word;
  }
`;

const Form = styled.form`
  padding: clamp(1.5rem, 3.5vw, 2.5rem);
  border-radius: var(--radius-lg);
  background: var(--bg-2);
  border: 1px solid var(--border-strong);
`;

const FormTitle = styled.h2`
  font-size: 1.35rem;
  margin-bottom: 0.4rem;
`;

const FormIntro = styled.p`
  color: var(--text-2);
  margin-bottom: 1.75rem;
  font-size: 0.95rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  margin-bottom: 1.25rem;

  label {
    display: block;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 0.45rem;
    color: var(--text-1);
  }

  label span {
    color: var(--text-3);
    font-weight: 400;
  }

  input,
  select,
  textarea {
    width: 100%;
    min-height: 48px;
    padding: 0.75rem 0.9rem;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-strong);
    background: rgba(255, 255, 255, 0.04);
    color: var(--text-1);
    font-size: 1rem;
    transition: border-color 0.18s ease, background-color 0.18s ease;
  }

  select option {
    background: var(--bg-1);
    color: var(--text-1);
  }

  textarea {
    min-height: 150px;
    resize: vertical;
  }

  input:focus-visible,
  select:focus-visible,
  textarea:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 1px;
    border-color: transparent;
    background: rgba(255, 255, 255, 0.06);
  }

  [aria-invalid='true'] {
    border-color: var(--error);
  }
`;

const FieldError = styled.p`
  margin-top: 0.4rem;
  font-size: var(--fs-small);
  color: var(--error);
`;

const Honeypot = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const Consent = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  margin: 0.25rem 0 0.4rem;

  input {
    width: 20px;
    height: 20px;
    margin-top: 0.15rem;
    accent-color: var(--lime-500);
    flex-shrink: 0;
  }

  label {
    font-size: 0.9rem;
    color: var(--text-2);
  }
`;

const FormError = styled.div`
  margin-bottom: 1.25rem;
  padding: 0.85rem 1rem;
  border-radius: var(--radius-sm);
  border: 1px solid rgba(255, 123, 123, 0.4);
  background: rgba(255, 123, 123, 0.08);
  color: var(--text-1);
  font-size: 0.95rem;
`;

const Submit = styled.div`
  margin-top: 1.5rem;
`;

const Success = styled.div`
  padding: clamp(1.75rem, 4vw, 2.5rem);
  border-radius: var(--radius-lg);
  background: var(--bg-2);
  border: 1px solid rgba(192, 204, 48, 0.4);

  svg.check {
    width: 44px;
    height: 44px;
    padding: 10px;
    border-radius: 50%;
    background: rgba(192, 204, 48, 0.12);
    color: var(--accent);
    margin-bottom: 1.25rem;
  }

  h2 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-2);
    margin-bottom: 1.5rem;
  }
`;

const Contact: React.FC = () => {
  usePageMeta(pages.contact);

  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useLayoutEffect(() => {
    const typed = readFormFromDom();
    if (!typed) return;
    setForm((current) => (sameForm(current, typed) ? current : typed));
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;
    const nextValue = type === 'checkbox' ? (event.target as HTMLInputElement).checked : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    if (errors[name as keyof FieldErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const focusFirstError = (fieldErrors: FieldErrors) => {
    const first = Object.keys(fieldErrors).find((key) => fieldErrors[key as keyof FieldErrors]);
    if (!first) return;
    formRef.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const fieldErrors = validate(form);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      focusFirstError(fieldErrors);
      return;
    }

    setSubmitting(true);
    const { consent, ...payload } = form;
    const result = await sendContactEmail({
      ...payload,
      firstName: payload.firstName.trim(),
      lastName: payload.lastName.trim(),
      email: payload.email.trim(),
      company: payload.company.trim(),
      message: payload.message.trim(),
    });
    setSubmitting(false);

    if (result.success) {
      setSubmitted(true);
      return;
    }

    setFormError(result.error);
    if (result.fields) {
      setErrors(result.fields);
      focusFirstError(result.fields);
    }
  };

  const fieldProps = (name: keyof FieldErrors) => ({
    id: name,
    name,
    'aria-invalid': errors[name] ? true : undefined,
    'aria-describedby': errors[name] ? `${name}-error` : undefined,
  });

  const errorFor = (name: keyof FieldErrors) =>
    errors[name] ? <FieldError id={`${name}-error`}>{errors[name]}</FieldError> : null;

  return (
    <Layout>
      <PageHero
        eyebrow="Contact"
        title="Let's talk about your project."
        lead="Book a consultation if you'd like to talk it through, or send a message and we'll get back to you by email."
      />

      <Section $tone="raised" aria-label="Contact options">
        <Container>
          <Grid>
            <Side>
              <SideCard>
                <h2>
                  <CalendarIcon /> Prefer to talk?
                </h2>
                <p>Pick a time that works for you. We'll use the call to understand your goals and constraints.</p>
                <BookConsultationButton size="lg" />
              </SideCard>
              <SideCard>
                <h2>
                  <MailIcon /> Email us
                </h2>
                <p>Write to us directly. It reaches the same team.</p>
                <a className="email" href={`mailto:${CONTACT_EMAIL}`}>
                  {CONTACT_EMAIL}
                </a>
              </SideCard>
            </Side>

            <div aria-live="polite">
              {submitted ? (
                <Success role="status">
                  <CheckIcon className="check" />
                  <h2>Thanks, your message is on its way.</h2>
                  <p>We'll reply by email. If you'd rather talk it through, you can book a consultation now.</p>
                  <BookConsultationButton />
                </Success>
              ) : (
                <Form ref={formRef} onSubmit={handleSubmit} noValidate aria-labelledby="contact-form-title">
                  <FormTitle id="contact-form-title">Send a message</FormTitle>
                  <FormIntro>All fields are required unless marked optional.</FormIntro>

                  {formError && <FormError role="alert">{formError}</FormError>}

                  <Row>
                    <Field>
                      <label htmlFor="firstName">First name</label>
                      <input
                        type="text"
                        autoComplete="given-name"
                        maxLength={LIMITS.name}
                        value={form.firstName}
                        onChange={handleChange}
                        {...fieldProps('firstName')}
                      />
                      {errorFor('firstName')}
                    </Field>
                    <Field>
                      <label htmlFor="lastName">Last name</label>
                      <input
                        type="text"
                        autoComplete="family-name"
                        maxLength={LIMITS.name}
                        value={form.lastName}
                        onChange={handleChange}
                        {...fieldProps('lastName')}
                      />
                      {errorFor('lastName')}
                    </Field>
                  </Row>

                  <Field>
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      value={form.email}
                      onChange={handleChange}
                      {...fieldProps('email')}
                    />
                    {errorFor('email')}
                  </Field>

                  <Field>
                    <label htmlFor="company">
                      Company <span>(optional)</span>
                    </label>
                    <input
                      type="text"
                      autoComplete="organization"
                      maxLength={LIMITS.company}
                      value={form.company}
                      onChange={handleChange}
                      {...fieldProps('company')}
                    />
                    {errorFor('company')}
                  </Field>

                  <Field>
                    <label htmlFor="service">Interested in</label>
                    <select value={form.service} onChange={handleChange} {...fieldProps('service')}>
                      <option value="">Select a service</option>
                      {SERVICE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errorFor('service')}
                  </Field>

                  <Field>
                    <label htmlFor="message">Your message</label>
                    <textarea
                      maxLength={LIMITS.message}
                      value={form.message}
                      onChange={handleChange}
                      {...fieldProps('message')}
                    />
                    {errorFor('message')}
                  </Field>

                  <Honeypot aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={handleChange}
                    />
                  </Honeypot>

                  <Consent>
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={handleChange}
                      {...fieldProps('consent')}
                    />
                    <label htmlFor="consent">I agree to be contacted by CuttingEdge DevOps about my inquiry.</label>
                  </Consent>
                  {errorFor('consent')}

                  <Submit>
                    <Button type="submit" size="lg" fullWidth disabled={submitting}>
                      {submitting ? 'Sending…' : 'Send message'}
                    </Button>
                  </Submit>
                </Form>
              )}
            </div>
          </Grid>
        </Container>
      </Section>
    </Layout>
  );
};

export default Contact;
